import React from "react"

import { Box, Grid } from "@mui/material"


import {
	ErrorResponse, Loading,
	RestResponse,
} from "@sinossi/mates-react-library"

import {
	Action,
	AssetMatchBrandWithImage,
	AssetProvenience,
	AssetWithImage,
	DashboardResponse, HotBrandResponse,
	NewAssetFilter,
	OrderType,
	PaginatedResponse,
	SessionResponse
} from "models"

import {
	DashboardCard,
	DashboardCardsData,
	DashboardCardWithList,
} from "."
import { AssetService } from "services/asset.service"
import { AccountServices } from "services/account.services"
import DashboardAssetsList from "components/dashboard/dashboard-assets-list/dashboard-assets-list"
import { AssetListOrigin } from "components/dashboard/dashboard-assets-list/model"
import { DashboardServices } from "services/dashboard.services"
import { VisitService } from "services/visit.service"
import { RecentAssetsService, GetRecentAssetsRequest } from "services/recent-assets.service"

interface DashboardAdminProps {
	sessionData: SessionResponse
	dashboardData: DashboardResponse
	actions: PaginatedResponse<Action> | null
	notifications: PaginatedResponse<Action> | null

	onException: (response: RestResponse<any, ErrorResponse>) => void
	refreshActions: () => void
}
interface DashboardAdminState {
	hotAsset: PaginatedResponse<AssetMatchBrandWithImage> | null
	hotBrand: PaginatedResponse<HotBrandResponse> | null
	visits: PaginatedResponse<any> | null
	newAssetFilter: NewAssetFilter
	visitFilter: any
	newAssets: AssetWithImage[] | null
	likes: string[]
	loading: boolean
	likedAssets: AssetWithImage[] | null
}
export class DashboardAdmin extends React.Component<DashboardAdminProps, DashboardAdminState> {
	constructor(props: DashboardAdminProps) {
		super(props)
		this.state = {
			hotAsset: null,
			hotBrand: null,
			visits: null,
			likedAssets: null,
			newAssetFilter: {
				pageSize: 50,
				pageId: 0,
				orderType: OrderType.DESCENDING,
				orderBy: "creationDate",
				provenance: null
			},
			visitFilter: {
				pageSize: 10,
				pageId: 0,
				orderType: OrderType.DESCENDING,
				orderBy: "creationDate"
			},
			newAssets: null,
			likes: [],
			loading: true
		}
	}

	public componentDidMount() {
		this.getHotAsset()
		this.getLikedAssets()
	}

	private getHotAsset = () => {
		DashboardServices.getHotAsset(this.getHotAssetCallback, this.props.onException)
	}

	private getHotBrand = () => {
		DashboardServices.getHotBrand(this.getHotBrandCallback, this.props.onException)
	}

	private getNewAssets = (newFilter?: AssetProvenience | null) => {
		const request: GetRecentAssetsRequest = {
			limit: 3, // Solo i 3 più recenti per la dashboard
			provenance: newFilter !== undefined ? newFilter : null,
			daysBack: 30 // Ultimi 30 giorni
		}

		RecentAssetsService.getRecentAssets(
			request,
			this.getNewAssetsCallback,
			this.props.onException
		)
	}

	private getLikedAssets = () => {
		AssetService.getLikedAssets(
			this.getLikedAssetsCallback,
			this.props.onException
		)
	}

	private getAssetLikes = () => {
		AccountServices.getAssetLikes(this.getAssetLikesCallback, this.props.onException)
	}

	private getVisits = () => {
		VisitService.filterVisits(
			this.state.visitFilter,
			this.getVisitsCallback,
			this.props.onException
		)
	}

	private getHotAssetCallback = (response: RestResponse<PaginatedResponse<AssetMatchBrandWithImage>, ErrorResponse>) => {
		if (response.hasError()) {
			this.props.onException(response)
			return
		}

		this.setState(() => ({
			hotAsset: response.data,
		}), this.getHotBrand)
	}

	private getHotBrandCallback = (response: RestResponse<PaginatedResponse<HotBrandResponse>, ErrorResponse>) => {
		if (response.hasError()) {
			this.props.onException(response)
			return
		}

		this.setState(() => ({
			hotBrand: response.data,
		}), this.getNewAssets)
	}

	private getLikedAssetsCallback = (response: RestResponse<any, ErrorResponse>) => {
		if (response.hasError()) {
			this.props.onException(response)
			return
		}

		this.setState(
			() => ({ likedAssets: response.data.items, }), () => setTimeout(() => {
				AccountServices.getAssetLikes(this.getAssetLikesCallback, this.props.onException)
			}, 100))
	}

	private getNewAssetsCallback = (response: RestResponse<any, ErrorResponse>) => {
		if (response.hasError()) {
			this.props.onException(response)
			return
		}

		// La nuova API restituisce direttamente una lista, non paginata per la dashboard
		const assets = response.data.items || response.data || []
		this.setState(
			() => ({ newAssets: assets }),
			this.getAssetLikes
		)
	}

	private getAssetLikesCallback = (response: any) => {
		if (response.hasError()) {
			this.props.onException(response)
			return
		}

		this.setState(() => ({
			likes: response.data,
		}), this.getVisits)
	}

	private getVisitsCallback = (response: RestResponse<PaginatedResponse<any>, ErrorResponse>) => {
		if (response.hasError()) {
			this.props.onException(response)
			return
		}

		this.setState(() => ({
			visits: response.data,
			loading: false
		}))
	}

	private refreshVisits = () => {
		this.getVisits()
	}

	render() {
		const statistics = this.props.dashboardData?.statistics

		return (
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					gap: 3
				}}
			>
				<Loading show={this.state.loading} />
				{
					!this.state.loading &&
					<>
						<Grid container spacing={2}>
							<Grid item lg={4}>
								<DashboardCard
									navigateTo={"/asset"}
									cardType={DashboardCardsData.ASSETS}
									value={statistics?.assets.value}
									percentage={statistics?.assets.variationPercentage}
									notifications={statistics?.assets.currentPeriodValue} />
							</Grid>
							<Grid item lg={4}>
								<DashboardCard
									navigateTo={"/match-proposal"}
									cardType={DashboardCardsData.SENT_MATCH}
									value={statistics?.sentMatches.value}
									percentage={statistics?.sentMatches.variationPercentage}
									notifications={statistics?.sentMatches.currentPeriodValue} />
							</Grid>
							<Grid item lg={4}>
								<DashboardCard
									navigateTo={"/contract"}
									cardType={DashboardCardsData.APPROVED_MATCH}
									value={statistics?.approvedMatches.value}
									percentage={statistics?.approvedMatches.variationPercentage}
									notifications={statistics?.approvedMatches.currentPeriodValue} />
							</Grid>
							<Grid item lg={6}>
								{this.state.hotAsset && <DashboardCardWithList
									navigateTo={"/match-proposal"}
									cardType={DashboardCardsData.HOT_ASSETS}
									data={this.state.hotAsset}
									refreshPage={this.props.refreshActions}
									sessionData={this.props.sessionData}
								/>}
							</Grid>
							<Grid item lg={6}>
								{this.state.hotBrand && <DashboardCardWithList
									navigateTo={"/"}
									cardType={DashboardCardsData.HOT_BRAND}
									data={this.state.hotBrand}
									refreshPage={this.props.refreshActions}
									sessionData={this.props.sessionData}
								/>}
							</Grid>
							<Grid item lg={6}>
								{
									this.props.actions &&
									<DashboardCardWithList
										navigateTo={"/actions"}
										cardType={DashboardCardsData.ACTIONS}
										data={this.props.actions}
										refreshPage={this.props.refreshActions}
										sessionData={this.props.sessionData}
									/>
								}
							</Grid>
							<Grid item lg={6}>
								{this.state.visits && <DashboardCardWithList
									navigateTo={"/visit"}
									cardType={DashboardCardsData.VISITS}
									data={this.state.visits}
									refreshPage={this.refreshVisits}
									sessionData={this.props.sessionData}
								/>}
							</Grid>
						</Grid>
						<DashboardAssetsList
							filter={this.getNewAssets}
							assetsList={this.state.newAssets || []}
							likes={this.state.likes}
							sessionData={this.props.sessionData}
							origin={AssetListOrigin.NEWS}
							refreshLikes={this.getLikedAssets}
						/>
						<DashboardAssetsList
							likes={this.state.likes ?? []}
							assetsList={this.state.likedAssets ?? []}
							sessionData={this.props.sessionData}
							origin={AssetListOrigin.FAVOURITES}
							refreshLikes={this.getLikedAssets}
						/>
					</>
				}
			</Box>
		)
	}
}